import { useState } from 'react'
import { Card } from '@radix-ui/themes'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type RevealVariant = 'privateKey' | 'phrase'

interface RevealWarningDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  variant: RevealVariant
}

const TOTAL_STEPS = 3

const RevealWarningDialog = ({
  open,
  onClose,
  onConfirm,
  variant
}: RevealWarningDialogProps) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(1)

  const prefix = variant === 'privateKey' ? 'key_warning' : 'phrase_gate'
  const revealLabelKey =
    variant === 'privateKey' ? 'recover.key_warning_reveal' : 'recover.phrase_gate_reveal'

  const handleCancel = () => {
    setStep(1)
    onClose()
  }

  const handleContinue = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1)
      return
    }
    setStep(1)
    onClose()
    onConfirm()
  }

  const title = t(`recover.${prefix}_${step}_title`)
  const desc = t(`recover.${prefix}_${step}_desc`)

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="shadow-xl bg-card w-full max-w-md justify-center relative">
          <div className="flex flex-col gap-6 p-4 place-self-center">
            <div className="flex flex-col gap-3 text-center items-center">
              <div className="bg-red-500/10 rounded-full p-4 w-fit border border-red-500/30">
                <TriangleAlert className="text-red-500 w-6 h-6" />
              </div>

              <p className="uppercase text-xs font-semibold text-red-500 tracking-[2.4px]">
                {t('recover.key_warning_step', {
                  current: step,
                  total: TOTAL_STEPS
                })}
              </p>

              <h2 className="text-text-main text-2xl font-bold">{title}</h2>

              <p className="text-text-secondary text-sm leading-relaxed">
                {desc}
              </p>
            </div>

            {/* Step progress dots */}
            <div className="flex gap-2 justify-center">
              {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    index < step ? 'w-8 bg-red-500' : 'w-4 bg-card-e2'
                  }`}
                />
              ))}
            </div>

            <div className="flex flex-col gap-4">
              <button
                onClick={handleContinue}
                className="btn-gradient-fill rounded-full py-3.5"
              >
                <p className="font-bold">
                  {step < TOTAL_STEPS
                    ? t('recover.key_warning_continue')
                    : t(revealLabelKey)}
                </p>
              </button>
              <button
                onClick={handleCancel}
                className="text-text-secondary text-sm py-2"
              >
                {t('dialogs.cancel')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default RevealWarningDialog
